/**
 * Detecta o sufixo "Z"/"z" (UTC explícito) no fim da string.
 */
const UTC_SUFFIX_REGEX = /[zZ]$/

/**
 * Detecta offset explícito (+HH:MM, -HH:MM, +HHMM, -HHMM) logo depois da parte
 * de hora. Exigir o "HH:MM" antes do sinal evita confundir os hifens da data
 * (ex.: "2026-07-27") com um offset negativo.
 */
const TIMEZONE_OFFSET_REGEX =
  /\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?\s*[+-]\d{2}:?\d{2}$/

/** Detecta se a string possui uma parte de hora ("HH:MM"). */
const TIME_PART_REGEX = /\d{2}:\d{2}/

/**
 * Converte um datetime vindo da API em `Date`.
 *
 * POR QUE o fallback é UTC: a API pode devolver datetimes "naive" (sem `Z` e
 * sem offset, ex.: "2026-07-27 14:30:00"). Nesse caso o JS interpretaria a
 * string como horário LOCAL do navegador — para um aluno em BRT (UTC-3) isso
 * desloca a data em 3 horas e faz o cronômetro da prova nascer vencido. Como o
 * backend grava em UTC, tratamos toda string sem timezone como UTC.
 *
 * @returns `Date` válido ou `null` (entrada vazia/inválida).
 */
export function parseApiDateTime(
  value: string | null | undefined
): Date | null {
  if (typeof value !== 'string') {
    return null
  }

  const trimmed = value.trim()
  if (trimmed.length === 0) {
    return null
  }

  const hasTimezone =
    UTC_SUFFIX_REGEX.test(trimmed) || TIMEZONE_OFFSET_REGEX.test(trimmed)

  // Só normalizamos quando há parte de hora: datas puras ("2026-07-27") já são
  // interpretadas como UTC pelo próprio JS.
  const shouldNormalize = !hasTimezone && TIME_PART_REGEX.test(trimmed)
  const normalized = shouldNormalize ? `${trimmed.replace(' ', 'T')}Z` : trimmed

  const date = new Date(normalized)
  return Number.isNaN(date.getTime()) ? null : date
}

/**
 * Mesma semântica de `parseApiDateTime`, porém devolvendo o timestamp em
 * milissegundos (ou `null` quando a data é ausente/inválida).
 */
export function parseApiDateTimeToMs(
  value: string | null | undefined
): number | null {
  const date = parseApiDateTime(value)
  return date === null ? null : date.getTime()
}
